import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Settings, DollarSign, Flag, ArrowLeft, Save, Check, RotateCcw, Trash2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Layout } from '@/components/wiki/Layout';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SuperAdminConfig() {
  const navigate = useNavigate();
  const { user, loading: authLoading, getAuthHeader, isSuperAdmin } = useAuth();
  const [pricing, setPricing] = useState({ monthly_price: 69.00, yearly_price: 599.00 });
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedPricing, setEditedPricing] = useState({ monthly_price: 69.00, yearly_price: 599.00 });

  useEffect(() => {
    if (authLoading) return;
    
    if (!user || !isSuperAdmin()) {
      navigate('/');
      return;
    }

    const fetchData = async () => {
      try {
        const [pricingRes, reportsRes] = await Promise.all([
          axios.get(`${API}/pricing`),
          axios.get(`${API}/reports`, { headers: getAuthHeader() })
        ]);
        setPricing(pricingRes.data);
        setEditedPricing(pricingRes.data);
        setReports(reportsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load configuration');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, authLoading, navigate, getAuthHeader, isSuperAdmin]);

  const handleSavePricing = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/pricing`, editedPricing, {
        headers: getAuthHeader()
      });
      setPricing(response.data);
      toast.success('Pricing updated successfully! Stripe products will use new prices.');
    } catch (error) {
      toast.error('Failed to update pricing');
    } finally {
      setSaving(false);
    }
  };

  const handleReviewReport = async (reportId, action) => {
    try {
      await axios.put(`${API}/reports/${reportId}/review?action=${action}`, {}, {
        headers: getAuthHeader()
      });
      
      const reportsRes = await axios.get(`${API}/reports`, { headers: getAuthHeader() });
      setReports(reportsRes.data);
      
      toast.success(`Report ${action === 'restore' ? 'resolved - page restored' : 'resolved - page deleted'}`);
    } catch (error) {
      toast.error(`Failed to ${action} report`);
    }
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-10 bg-[#eaecf0] rounded w-1/4" />
            <div className="h-64 bg-[#eaecf0] rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  const pendingReports = reports.filter(r => r.status === 'pending');
  const monthlyEquivalent = (editedPricing.yearly_price / 12).toFixed(2);
  const savings = ((editedPricing.monthly_price * 12) - editedPricing.yearly_price).toFixed(0);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="super-admin-config">
        {/* Header */}
        <div className="mb-8">
          <Link to="/admin" className="text-sm text-[#36c] hover:underline flex items-center gap-1 mb-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Admin Dashboard
          </Link>
          <h1 className="font-serif text-3xl font-bold text-[#202122] flex items-center gap-3">
            <Settings className="h-8 w-8 text-[#36c]" />
            Super Admin Configuration
          </h1>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="pricing" className="bg-white rounded-lg border border-[#a2a9b1]">
          <TabsList className="border-b border-[#c8ccd1] w-full justify-start rounded-none p-0 bg-transparent">
            <TabsTrigger 
              value="pricing" 
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#36c] data-[state=active]:bg-transparent text-[#54595d] data-[state=active]:text-[#202122]" 
              data-testid="pricing-tab"
            >
              <DollarSign className="h-4 w-4 mr-2" />
              Pricing
            </TabsTrigger>
            <TabsTrigger 
              value="reports" 
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#36c] data-[state=active]:bg-transparent text-[#54595d] data-[state=active]:text-[#202122]" 
              data-testid="reports-tab"
            >
              <Flag className="h-4 w-4 mr-2" />
              Review Reports
              {pendingReports.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-[#d33] text-white text-xs rounded-full">
                  {pendingReports.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Pricing Tab */}
          <TabsContent value="pricing" className="p-6">
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-[#202122] mb-4">Claim Page Pricing</h3>
                <p className="text-sm text-[#54595d] mb-6">
                  Set the monthly and yearly subscription prices for claiming pages. Changes will update Stripe checkout prices immediately.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Monthly Price */}
                <div className="space-y-2">
                  <Label htmlFor="monthly-price" className="text-[#202122]">Monthly Price (USD)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#72777d]">$</span>
                    <Input
                      id="monthly-price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={editedPricing.monthly_price}
                      onChange={(e) => setEditedPricing({ ...editedPricing, monthly_price: parseFloat(e.target.value) || 0 })}
                      className="pl-8 bg-white border-[#a2a9b1] text-[#202122]"
                      data-testid="monthly-price-input"
                    />
                  </div>
                  <p className="text-xs text-[#72777d]">per month</p>
                </div>

                {/* Yearly Price */}
                <div className="space-y-2">
                  <Label htmlFor="yearly-price" className="text-[#202122]">Yearly Price (USD)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#72777d]">$</span>
                    <Input
                      id="yearly-price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={editedPricing.yearly_price}
                      onChange={(e) => setEditedPricing({ ...editedPricing, yearly_price: parseFloat(e.target.value) || 0 })}
                      className="pl-8 bg-white border-[#a2a9b1] text-[#202122]"
                      data-testid="yearly-price-input"
                    />
                  </div>
                  <p className="text-xs text-[#72777d]">${monthlyEquivalent}/month • Save ${savings}/year</p>
                </div>
              </div>

              {/* Preview */}
              <div className="bg-[#f8f9fa] rounded-lg p-4 border border-[#c8ccd1]">
                <h4 className="text-sm font-medium text-[#54595d] mb-4">Pricing Preview</h4>
                <div className="flex gap-4">
                  <div className="flex-1 bg-white rounded-lg p-4 border border-[#a2a9b1]">
                    <p className="text-sm text-[#54595d]">Monthly</p>
                    <p className="text-2xl font-bold text-[#202122]">${editedPricing.monthly_price}</p>
                  </div>
                  <div className="flex-1 bg-white rounded-lg p-4 border-2 border-[#36c]">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-[#54595d]">Yearly</p>
                      <span className="text-xs bg-[#36c] text-white px-2 py-0.5 rounded">Best Value</span>
                    </div>
                    <p className="text-2xl font-bold text-[#202122]">${editedPricing.yearly_price}</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setEditedPricing(pricing)}
                  className="border-[#a2a9b1] text-[#54595d] hover:bg-[#eaecf0]"
                  data-testid="reset-pricing-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reset
                </Button>
                <Button 
                  onClick={handleSavePricing}
                  disabled={saving}
                  className="bg-[#36c] text-white hover:bg-[#2a4b8d]"
                  data-testid="save-pricing-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Pricing'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="p-6">
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-[#202122] mb-2">Pending Reports</h3>
                <p className="text-sm text-[#54595d]">
                  Review and take action on reported content. Reported pages are temporarily removed until reviewed.
                </p>
              </div>

              {pendingReports.length === 0 ? (
                <div className="text-center py-12 bg-[#f8f9fa] rounded-lg border border-[#c8ccd1]">
                  <Check className="h-12 w-12 mx-auto text-[#14866d] mb-4" />
                  <p className="text-[#202122]">No pending reports</p>
                  <p className="text-sm text-[#54595d]">All reports have been reviewed</p>
                </div>
              ) : (
                <div className="border border-[#c8ccd1] rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#c8ccd1] bg-[#f8f9fa]">
                        <TableHead className="text-[#54595d]">Issue</TableHead>
                        <TableHead className="text-[#54595d]">Page</TableHead>
                        <TableHead className="text-[#54595d]">Description</TableHead>
                        <TableHead className="text-[#54595d]">Date</TableHead>
                        <TableHead className="text-[#54595d]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingReports.map((report) => (
                        <TableRow key={report.id} className="border-[#c8ccd1] hover:bg-[#f8f9fa]" data-testid={`report-row-${report.id}`}>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              report.issue_type === 'copyright' ? 'bg-[#eaf3ff] text-[#36c]' :
                              report.issue_type === 'privacy' ? 'bg-[#f3e8ff] text-[#7c3aed]' :
                              report.issue_type === 'defamation' ? 'bg-[#fee7e6] text-[#d33]' :
                              report.issue_type === 'harassment' ? 'bg-[#fef6e7] text-[#ac6600]' :
                              'bg-[#eaecf0] text-[#54595d]'
                            }`}>
                              {report.issue_type}
                            </span>
                          </TableCell>
                          <TableCell className="font-medium text-[#202122]">{report.page_title || 'N/A'}</TableCell>
                          <TableCell className="max-w-xs truncate text-[#54595d]">{report.description}</TableCell>
                          <TableCell className="text-[#54595d]">{new Date(report.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-[#14866d] border-[#14866d] hover:bg-[#d5fdd5]"
                                onClick={() => handleReviewReport(report.id, 'restore')}
                                title="Restore page"
                                data-testid={`restore-btn-${report.id}`}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-[#d33] border-[#d33] hover:bg-[#fee7e6]"
                                onClick={() => handleReviewReport(report.id, 'delete')}
                                title="Delete page permanently"
                                data-testid={`delete-btn-${report.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* All Reports History */}
              <div className="mt-8">
                <h4 className="font-medium text-[#202122] mb-4">All Reports History</h4>
                <div className="border border-[#c8ccd1] rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#c8ccd1] bg-[#f8f9fa]">
                        <TableHead className="text-[#54595d]">Issue</TableHead>
                        <TableHead className="text-[#54595d]">Page</TableHead>
                        <TableHead className="text-[#54595d]">Status</TableHead>
                        <TableHead className="text-[#54595d]">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reports.length === 0 ? (
                        <TableRow className="border-[#c8ccd1]">
                          <TableCell colSpan={4} className="text-center text-[#54595d] py-8">
                            No reports yet
                          </TableCell>
                        </TableRow>
                      ) : (
                        reports.map((report) => (
                          <TableRow key={report.id} className="border-[#c8ccd1] hover:bg-[#f8f9fa]">
                            <TableCell>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                report.issue_type === 'copyright' ? 'bg-[#eaf3ff] text-[#36c]' :
                                report.issue_type === 'privacy' ? 'bg-[#f3e8ff] text-[#7c3aed]' :
                                report.issue_type === 'defamation' ? 'bg-[#fee7e6] text-[#d33]' :
                                report.issue_type === 'harassment' ? 'bg-[#fef6e7] text-[#ac6600]' :
                                'bg-[#eaecf0] text-[#54595d]'
                              }`}>
                                {report.issue_type}
                              </span>
                            </TableCell>
                            <TableCell className="font-medium text-[#202122]">{report.page_title || 'N/A'}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                report.status === 'pending' ? 'bg-[#fef6e7] text-[#ac6600]' :
                                report.status === 'resolved' ? 'bg-[#d5fdd5] text-[#14866d]' :
                                'bg-[#eaecf0] text-[#54595d]'
                              }`}>
                                {report.status}
                              </span>
                            </TableCell>
                            <TableCell className="text-[#54595d]">{new Date(report.created_at).toLocaleDateString()}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
